<template>
  <section ref="root" class="manifesto" aria-label="Sobre Coppados">
    <p class="eyebrow manifesto__eyebrow" data-reveal>(Coppados)</p>

    <p ref="text" class="manifesto__text" :aria-label="plainText">
      <template v-for="(item, i) in tokens">
        <span v-if="item.img" :key="i" class="manifesto__image" data-manifesto aria-hidden="true">
          <img :src="item.img" alt="" loading="lazy">
        </span>
        <span
          v-else
          :key="i"
          class="manifesto__word"
          :class="{ 'is-accent': item.accent }"
          data-manifesto
          aria-hidden="true"
        >{{ item.word }}</span>
        {{ ' ' }}
      </template>
    </p>

    <div class="manifesto__pillars">
      <div v-for="(pillar, i) in pillars" :key="pillar.title" class="pillar" data-reveal>
        <span class="pillar__index">0{{ i + 1 }}</span>
        <h3 class="pillar__title">{{ pillar.title }}</h3>
        <p class="pillar__text">{{ pillar.text }}</p>
      </div>
    </div>
  </section>
</template>

<script>
import { gsap, prefersReducedMotion } from '@/animations/gsap'
import motion from '@/mixins/motion'
import nightImg from '@/assets/manifesto/night.webp'
import artistImg from '@/assets/manifesto/artist.webp'
import releaseImg from '@/assets/manifesto/release.webp'

const words = (text, accent = false) => text.split(' ').map((word) => ({ word, accent }))

export default {
  name: 'ManifestoSection',
  mixins: [motion],
  data() {
    const tokens = [
      ...words('Coppados is a record label,'),
      ...words('a party'),
      { img: nightImg },
      ...words('and a family of artists'),
      { img: artistImg },
      ...words('making music'),
      { img: releaseImg },
      ...words('hecho en Rosario,', true),
      ...words('para gente coppada.', true)
    ]
    return {
      tokens,
      pillars: [
        { title: 'Label', text: 'Original releases from Rosario to the world.' },
        { title: 'Events', text: 'Coppados presents: nights for gente coppada.' },
        { title: 'Artists', text: 'Our roster, available for bookings worldwide.' }
      ]
    }
  },
  computed: {
    plainText() {
      return this.tokens.filter((t) => t.word).map((t) => t.word).join(' ')
    }
  },
  mounted() {
    this.revealContent()
    this.motion(() => {
      if (prefersReducedMotion()) return
      const items = this.$refs.text.querySelectorAll('[data-manifesto]')
      const tl = gsap.timeline({
        scrollTrigger: { trigger: this.$refs.text, start: 'top 85%', end: 'bottom 60%', scrub: 0.6 }
      })
      items.forEach((item, i) => {
        if (item.classList.contains('manifesto__image')) {
          tl.fromTo(item, { scale: 0, rotate: -12 }, { scale: 1, rotate: 0, duration: 2, ease: 'back.out(2)' }, i)
        } else {
          tl.fromTo(item, { opacity: 0.14 }, { opacity: 1, duration: 1, ease: 'none' }, i)
        }
      })
    })
  }
}
</script>

<style scoped>
.manifesto {
  position: relative;
  z-index: 1;
  background: var(--c-bg);
  padding: calc(var(--section-space) * 0.8) var(--gutter) calc(var(--section-space) * 0.6);
}

.manifesto__eyebrow {
  margin-bottom: 1.75rem;
}

.manifesto__text {
  font-size: clamp(1.9rem, 0.9rem + 3.75vw, 5.2rem);
  line-height: 1.08;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  max-width: 30ch;
}

.manifesto__word {
  display: inline-block;
}

.manifesto__word.is-accent {
  color: var(--c-accent);
}

.manifesto__image {
  display: inline-block;
  width: 1.6em;
  height: 0.82em;
  border-radius: 999px;
  overflow: hidden;
  vertical-align: -0.04em;
  background: var(--c-surface);
}

.manifesto__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.manifesto__pillars {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2rem;
  margin-top: clamp(2.5rem, 1.5rem + 3vw, 4.5rem);
}

.pillar {
  border-top: 1px solid var(--c-line);
  padding-top: 1.25rem;
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 1.25rem;
  row-gap: 0.6rem;
}

.pillar__index {
  grid-row: span 2;
  font-size: var(--fs-small);
  color: var(--c-accent);
  letter-spacing: 0.1em;
  padding-top: 0.35em;
}

.pillar__title {
  font-size: var(--fs-h3);
  font-weight: 400;
  text-transform: uppercase;
  line-height: 1;
}

.pillar__text {
  color: var(--c-muted);
  font-size: var(--fs-body);
  line-height: 1.45;
  max-width: 30ch;
}

@media (max-width: 760px) {
  .manifesto__pillars {
    grid-template-columns: 1fr;
  }
}
</style>
